import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver,
  ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';

const COLOR = '#006B8F';

const PROCESS = [
  {
    num:'01', title:'Connectivity Assessment',
    desc:'Connectivity is more than just WiFi. We evaluate your deployment environment, device count, data volume, power constraints, and geographic coverage to recommend the right protocol — because the wrong choice here cascades through the entire product. LoRaWAN for wide-area low-power sensors is a very different recommendation from BLE for short-range consumer devices.',
    points:[
      'Deployment environment and coverage area analysis',
      'Device count, data rate, and message frequency planning',
      'Power budget assessment — WiFi vs BLE vs LoRaWAN vs NB-IoT',
      'India network availability check for cellular and LPWAN',
    ],
  },
  {
    num:'02', title:'Cloud Platform Selection & Setup',
    desc:'We select the cloud IoT platform that best fits your budget, compliance requirements, and technical constraints — AWS IoT Core, Azure IoT Hub, Google Cloud IoT, ThingsBoard, or EMQX for on-premise. Then we set up the core infrastructure: IoT broker, device registry, message routing, and data storage.',
    points:[
      'Cloud platform evaluation against budget and compliance requirements',
      'IoT Core or message broker setup and configuration',
      'Device registry, topic architecture, and message routing rules',
      'Data storage architecture — time-series DB, data lake, or hybrid',
    ],
  },
  {
    num:'03', title:'Device Provisioning & Authentication',
    desc:'Every deployed device needs a unique, cryptographically verified identity. We build device provisioning workflows with certificate-based authentication, automated certificate rotation, and scalable onboarding — so adding device 10,000 is as easy as adding device one.',
    points:[
      'Certificate authority setup and device certificate generation',
      'Certificate-based mutual TLS (mTLS) device authentication',
      'Automated provisioning workflow for manufacturing and field deployment',
      'Certificate rotation and revocation for security lifecycle management',
    ],
  },
  {
    num:'04', title:'Data Pipeline & Storage Design',
    desc:'Raw device data is useless without a well-designed pipeline to ingest, parse, route, and store it. We build MQTT/HTTPS/CoAP ingestion pipelines with payload parsing, normalization, and routing to time-series databases, relational stores, or data lakes — with query patterns optimised for your dashboard and analytics needs.',
    points:[
      'MQTT, HTTPS, and CoAP data ingestion pipeline build',
      'Payload parsing, normalization, and schema enforcement',
      'Time-series database setup (InfluxDB, TimescaleDB) with query optimisation',
      'Rule engine for automated triggers, alerts, and data routing',
    ],
  },
  {
    num:'05', title:'Monitoring, Alerts & Security Hardening',
    desc:'A production IoT cloud needs observability and hardening before you go live — not after your first incident. We implement device health monitoring, data anomaly alerting, IAM policies, network segmentation, and encryption at rest and in transit. Your cloud will be ready for a security audit on day one.',
    points:[
      'Device health monitoring — connectivity status, heartbeat, and battery',
      'Data anomaly alerting with configurable thresholds and escalation',
      'IAM policies, VPC network segmentation, and least-privilege access',
      'Encryption at rest and in transit — TLS, DTLS, and storage encryption',
    ],
  },
];

const DELIVERABLES = [
  { title:'Connectivity Protocol Report', desc:'Written recommendation covering WiFi, BLE, LoRaWAN, NB-IoT, LTE-M, GSM/GPRS, Zigbee, RS485/Modbus — with trade-off analysis for your specific deployment context.' },
  { title:'Cloud Architecture Design Document', desc:'Full IoT cloud architecture document — scalable from 10 devices to 100,000+ — with data flow diagrams, component choices, and cost model.' },
  { title:'IoT Core & Message Broker Setup', desc:'Configured AWS IoT Core, Azure IoT Hub, ThingsBoard, or EMQX with message routing, device shadow management, and topic architecture.' },
  { title:'Device Provisioning System', desc:'Certificate management, device identity provisioning, and automated onboarding workflows built for production scale and security.' },
  { title:'MQTT/HTTPS/CoAP Ingestion Pipeline', desc:'Data ingestion pipeline with payload parsing, normalization, and routing to your chosen time-series or relational storage.' },
  { title:'Time-Series Database Setup', desc:'InfluxDB or TimescaleDB configured and optimised for your data volume with efficient query patterns for dashboard and analytics use.' },
  { title:'Rule Engine & Automation Workflows', desc:'Alert triggers, automated actions, and workflow automation responding to device events, data thresholds, and connectivity state.' },
  { title:'Cloud Security Audit & Hardening', desc:'IAM policy implementation, network segmentation, encryption setup, and security review documentation — field-ready from day one.' },
];

const CASE_STUDIES = [
  {
    tag: 'Utilities',
    title: 'LoRaWAN Smart Water Meter Network',
    metric: '1,200 meters connected',
    desc: 'Deployed a private LoRaWAN network across a mid-sized Indian city for a water utility — gateway placement, ThingsBoard cloud setup, hourly meter data ingestion, tamper detection alerting, and a utility management portal.',
  },
  {
    tag: 'Industrial',
    title: 'AWS IoT Core for Plant Monitoring',
    metric: '85 devices, <3s data latency',
    desc: 'Designed and deployed an AWS IoT Core architecture for a manufacturing plant — MQTT message routing, device shadow management, InfluxDB time-series storage, and CloudWatch alerting for 85 sensor nodes.',
  },
  {
    tag: 'Smart Buildings',
    title: 'Multi-Tenant BMS Cloud Platform',
    metric: '12 buildings on one platform',
    desc: 'Multi-tenant IoT cloud on Azure IoT Hub for a building management company — per-tenant data isolation, BACnet/Modbus protocol bridge, and REST API for integration with existing facility management software.',
  },
];

const FAQS = [
  {
    q: 'Which IoT cloud platforms do you work with?',
    a: 'We work with AWS IoT Core, Azure IoT Hub, Google Cloud IoT, and open-source platforms like ThingsBoard and EMQX for on-premise or hybrid deployments. Platform selection is driven by your budget, compliance requirements, geographic data residency needs, and technical constraints — not a standard recommendation.',
  },
  {
    q: 'What wireless connectivity protocols do you support?',
    a: 'We work with the full spectrum of IoT connectivity — WiFi, BLE, LoRaWAN, NB-IoT, LTE-M, GSM/GPRS, Zigbee, and RS485/Modbus for industrial brownfield integration. We also evaluate India-specific network availability for LPWAN protocols before recommending a solution, as coverage varies significantly by region.',
  },
  {
    q: 'How do you secure IoT devices connecting to the cloud?',
    a: 'We implement certificate-based mutual TLS (mTLS) authentication, giving each device a unique cryptographic identity. All communication is encrypted in transit using TLS. We also implement IAM policies with least-privilege access, network segmentation, and encryption at rest for stored device data. Security is designed from the start, not added as a patch.',
  },
  {
    q: 'Can your cloud architecture scale from a small pilot to 100,000 devices?',
    a: 'Yes. We design IoT cloud architectures to be horizontally scalable from the beginning — using event-driven message brokers, auto-scaling compute, and time-series databases that handle high-frequency writes efficiently. The architecture we deliver for a 50-device pilot is the same one that will handle 100,000 devices without a redesign.',
  },
  {
    q: 'Do you support LoRaWAN and NB-IoT for wide-area, low-power deployments?',
    a: 'Yes. We have deployed LoRaWAN networks using TTN (The Things Network) and private LoRaWAN servers, and we work with NB-IoT and LTE-M SIM providers active in India. For each project, we assess actual network coverage in the deployment region and recommend accordingly.',
  },
];

const PROTOCOLS = ['WiFi','BLE','LoRaWAN','NB-IoT','LTE-M','GSM/GPRS','Zigbee','RS485/Modbus'];
const PLATFORMS = ['AWS IoT Core','Azure IoT Hub','Google Cloud IoT','ThingsBoard','EMQX'];

function HeroVisual() {
  return (
    <img src="/visuals/services/Service 3.png" alt="IoT Cloud Integration & Connectivity" style={{ width:'100%', display:'block' }} />
  );
}

export default function IoTCloudPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT Cloud Integration Services India — Zone-X</title>
        <meta name="description" content="Zone-X provides IoT cloud integration and connectivity services in India — AWS IoT, Azure IoT Hub, MQTT pipelines, LoRaWAN networks, and secure device provisioning at scale." />
        <link rel="canonical" href="https://zone-x.dev/iot-cloud-integration" />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag="Services / IoT Cloud Integration"
          breadcrumb="Cloud Integration"
          title="IoT Cloud Integration &"
          titleAccent="Connectivity"
          subtitle="Your devices are ready. Now let's connect them to the world. Connectivity is more than just WiFi — the right protocol, cloud platform, security architecture, and data pipeline determine whether your IoT deployment scales to 10,000 devices or breaks at 100. Zone-X gets this right from the start."
          geo="IoT cloud integration and connectivity services for businesses in Bangalore, Karnataka, and across India."
          color={COLOR}
          visible={heroVisible}
          visual={<HeroVisual />}
        />
      </div>

      <StatsRow />
      <ConnectivityStack color={COLOR} protocols={PROTOCOLS} platforms={PLATFORMS} />
      <ProcessSection steps={PROCESS} color={COLOR} />
      <WhatWeDeliver items={DELIVERABLES} color={COLOR} />
      <ServiceCaseStudies studies={CASE_STUDIES} color={COLOR} />
      <ServiceFAQ faqs={FAQS} color={COLOR} />
      <IndustriesSection />
      <WhyZoneX />
      <ServiceCTA />
      <Footer />
    </>
  );
}

function ConnectivityStack({ color, protocols, platforms }) {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding:'0 0 clamp(40px,5vw,64px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'clamp(16px,2.4vw,24px)', opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }} className="conn-two-col">
          <div style={{ background:'rgba(255,255,255,0.8)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:20, padding:'clamp(24px,3vw,36px)' }}>
            <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10, color:color, textTransform:'uppercase' }}>CONNECTIVITY PROTOCOLS</span>
            <p style={{ fontSize:14, lineHeight:1.72, color:'var(--ink-soft)', margin:'12px 0 20px' }}>We match the connectivity solution to your use case — range, power, data rate, cost, and India network availability all factor in. There is no one-size-fits-all answer.</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {protocols.map((p,i) => (
                <span key={p} style={{ padding:'6px 13px', background:`${color}0C`, border:`1px solid ${color}22`, borderRadius:999, fontSize:12, fontWeight:600, color:'var(--ink)', opacity:visible?1:0, transform:visible?'none':'scale(0.9)', transition:`opacity 350ms ${i*40+100}ms, transform 350ms ${i*40+100}ms` }}>{p}</span>
              ))}
            </div>
          </div>
          <div style={{ background:'rgba(255,255,255,0.8)', border:'1px solid rgba(10,10,20,0.08)', borderRadius:20, padding:'clamp(24px,3vw,36px)' }}>
            <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10, color:color, textTransform:'uppercase' }}>CLOUD PLATFORMS</span>
            <p style={{ fontSize:14, lineHeight:1.72, color:'var(--ink-soft)', margin:'12px 0 20px' }}>We deploy event-driven, scalable, and secure architectures on the platform that best fits your budget, compliance requirements, and technical needs.</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {platforms.map((p,i) => (
                <span key={p} style={{ padding:'6px 13px', background:`${color}0C`, border:`1px solid ${color}22`, borderRadius:999, fontSize:12, fontWeight:600, color:'var(--ink)', opacity:visible?1:0, transform:visible?'none':'scale(0.9)', transition:`opacity 350ms ${i*40+180}ms, transform 350ms ${i*40+180}ms` }}>{p}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.conn-two-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
